import { readCompletionLink, completionLensType, completionSubmitUrl, buildCompletionPayload, submitCompletion, completionSuccessMessage } from '@theme/solstice-rx-completion';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { NAVIGATE_EVENT, StepNavigator } from '@theme/solstice-rx-navigator';
import { formatMoney } from '@theme/money-formatting';
import { buildAddRequest, buildFormDataRequest, generateLinkId } from '@theme/solstice-rx-cart';
import { buildCartAttributes, buildPrescription, buildPrismSummary } from '@theme/solstice-rx-order';
import { buildPricing, priceOfOption } from '@theme/solstice-rx-pricing';
import {
  RX_ANALYTICS_STEPS,
  deviceType,
  newRxAnalyticsSessionId,
  publishRxAnalytics,
  pupillaryDistanceSource,
  stepSelection,
} from '@theme/solstice-rx-analytics';
import {
  buildUploadFilename,
  describeCameraProblem,
  describeUploadProblem,
  readFileAsBase64,
  readUploadResult,
  uploadRetryHint,
  UPLOAD_TIMEOUT_MS,
} from '@theme/solstice-rx-upload';
import { measureVeeroPupillaryDistance, presentVeeroOverRx } from '@theme/solstice-rx-veero';
import {
  ADD_ON_FIELDS,
  buildPrescriptionTable,
  buildRxSteps,
  buildCompletionSteps,
  buildSelectionSummary,
  describeLensColor,
  findIncompatibility,
  findStepMarkupDrift,
  LENS_TYPES,
  needsClearLensConfirmation,
  PRISM_BLANK,
  readStepValues,
  shouldAutoAdvanceOption,
} from '@theme/solstice-rx-steps';

/**
 * How hard a photograph of a prescription is compressed.
 *
 * It is text on white, which survives compression far better than a picture of
 * a face would, and every tenth of quality here is megabytes off a phone upload.
 */
const CAMERA_PICTURE_QUALITY = 0.85;

/** How long the selection panel takes to open or close, in milliseconds. */
const SUMMARY_ANIMATION_MS = 250;

/** Whether the shopper has asked their system to keep movement to a minimum. */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * The lens configurator overlay.
 *
 * Its job is narrow: open when the launcher asks, keep the markup showing
 * whichever step the navigator says is current, and hand what the shopper types
 * back to the navigator. It makes no decisions about the flow itself — which
 * steps exist, which apply, and what each one demands all live in
 * `solstice__rx-steps.js`.
 *
 * @typedef {Object} RxConfiguratorRefs
 * @property {HTMLDialogElement} overlay
 * @property {HTMLFormElement[]} steps
 * @property {HTMLButtonElement} continueButton
 * @property {HTMLButtonElement} [backButton]
 * @property {HTMLElement} [summaryList]
 * @property {HTMLDetailsElement} [summaryDisclosure]
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [reviewLenses]
 * @property {HTMLElement} [reviewPrescription]
 * @property {HTMLElement} [reviewPrescriptionSection]
 * @property {HTMLVideoElement} [cameraPreview]
 * @property {HTMLElement} [cameraPlaceholder]
 * @property {HTMLElement} [uploadBar]
 * @property {HTMLElement} [uploadBarFill]
 * @property {HTMLElement} [uploadFailure]
 * @property {HTMLElement} [uploadHint]
 * @property {HTMLDialogElement} [clearLensDialog]
 *
 * @extends {Component<RxConfiguratorRefs>}
 */
class RxConfigurator extends Component {
  requiredRefs = ['overlay', 'steps', 'continueButton'];

  /** @type {StepNavigator | null} */
  #navigator = null;
  #completionError = '';
  #completionSubmitted = false;

  get isCompletion() { return this.dataset.completion === 'true'; }

  get usesUploadEndpoint() { return this.isCompletion || this.dataset.uploadMethod === 'google_script'; }

  /** Analytics: one id per page view, how PD and an upload were given, step timing. */
  #analyticsSessionId = newRxAnalyticsSessionId();
  #pdMeasuredWithVeero = false;
  /** @type {'photo' | 'file' | null} */
  #uploadSource = null;
  #stepEnteredAt = null;
  #openedOnce = false;

  /** Section whose product-select events carry the selected variant. */
  #section = null;

  /** Ignores product-select responses superseded by a newer selection. */
  #variantGeneration = 0;

  /** @type {import('@theme/solstice-rx-steps').IncompatiblePair[]} */
  #incompatibleOptions = [];

  /** @type {Map<string, { title: string, price: number }>} */
  #optionLookup = new Map();

  /** How full the bar was drawn last time, so the next step grows from there. */
  #shownProgress = 0;

  /** Steps whose rendered defaults have already been taken up. */
  #adoptedSteps = new Set();

  /** True only while seeding a step, to swallow the render that seeding causes. */
  #isAdopting = false;

  /** @type {Animation | null} */
  #summaryAnimation = null;

  /**
   * Which way a running summary animation is heading, or null when none is.
   * Not the same as the panel's own `open`, which lags behind while closing.
   *
   * @type {boolean | null}
   */
  #summaryTargetOpen = null;

  /**
   * The handle this configuration goes into the cart under.
   *
   * Made once and kept across a failed add and its retry, so one pair of
   * glasses does not end up filed under two. Cleared once it is in the bag, so
   * the next configuration gets a handle of its own.
   *
   * @type {string | null}
   */
  #linkId = null;

  /** True while a cart request is in flight, so a second click is ignored. */
  #isAddingToCart = false;

  /** The camera, while it is open. Every track must be stopped by hand. */
  #cameraStream = null;

  /** The upload in flight, kept so discarding can stop it. */
  #uploadRequest = null;
  #uploadController = null;
  #uploadGeneration = 0;

  /** The breakpoint that swaps blank select labels between desktop and mobile. */
  #mobileSelectLabels = null;

  #summaryDefaultApplied = false;

  #headerObserver = null;
  #backgroundInert = [];

  #restoreBackground = () => {
    for (const element of this.#backgroundInert) element.inert = false;
    this.#backgroundInert = [];
  };

  #handleEscape = (event) => {
    if (event.key !== 'Escape' || !this.refs.overlay.open || this.refs.overlay.dataset.showHeader !== 'true') return;
    // Header drawers and RX confirmation dialogs handle their own Escape.
    if (event.defaultPrevented || document.querySelector(':modal') || document.querySelector('#header-group [aria-expanded="true"]')) return;
    event.preventDefault();
    this.close();
  };

  #disableBackground() {
    const header = document.getElementById('header-group');
    let branch = this.refs.overlay;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (!(sibling instanceof HTMLElement) || sibling === branch || sibling.inert) continue;
        if (sibling === header || sibling.contains(header) || sibling.matches('dialog, script, style') || sibling.querySelector('dialog')) continue;
        sibling.inert = true;
        this.#backgroundInert.push(sibling);
      }
      branch = branch.parentElement;
    }
  }

  #updateHeaderHeight = () => {
    const header = document.getElementById('header-group');
    const height = this.refs.overlay.dataset.showHeader === 'true' ? header?.getBoundingClientRect().height ?? 0 : 0;
    this.refs.overlay.style.setProperty('--rx-header-height', `${height}px`);
  };

  connectedCallback() {
    super.connectedCallback();

    this.#incompatibleOptions = this.#readIncompatibleOptions();
    this.#optionLookup = this.#readOptionLookup();
    const steps = this.isCompletion ? buildCompletionSteps() : buildRxSteps({ incompatibleOptions: this.#incompatibleOptions });
    let lensTypeHandle;
    if (this.isCompletion) {
      try {
        readCompletionLink(window.location.search);
        completionSubmitUrl(this.dataset.completionBase, window.location.origin);
        lensTypeHandle = completionLensType(
          new URLSearchParams(window.location.search).get('lens_type'),
          this.dataset.initialLensType,
          Array.from(this.querySelectorAll('[data-completion-lens-types] input')).map((input) => ({
            handle: input.value, title: input.dataset.optionTitle,
          }))
        );
      } catch (error) { this.#completionError = error.message; }
    }

    this.#navigator = new StepNavigator({
      steps,
      // Context: known before the first step, owned by no step, and so never
      // part of what reaches the order.
      data: {
        ...(this.isCompletion ? { lensTypeHandle: lensTypeHandle || 'single-vision' } : {}),
        ageGroup: this.dataset.ageGroup === 'children' ? 'children' : 'adult',
        frameIsSunglasses: this.dataset.frameIsSunglasses === 'true',
      },
    });

    this.#navigator.addEventListener(NAVIGATE_EVENT, this.#render);
    document.addEventListener('solstice:rx:open', this.#handleOpenRequest);
    document.addEventListener('keydown', this.#handleEscape);
    this.addEventListener('change', this.#handleStepInput);
    this.refs.overlay.addEventListener('close', this.#handleOverlayClosed);
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#mobileSelectLabels = window.matchMedia('(max-width: 749px)');
    if (!this.#summaryDefaultApplied && this.refs.summaryDisclosure) {
      this.refs.summaryDisclosure.open = !this.#mobileSelectLabels.matches;
      this.#summaryDefaultApplied = true;
    }
    this.#mobileSelectLabels.addEventListener('change', this.#updateResponsiveSelectLabels);
    this.#updateResponsiveSelectLabels();

    if (!this.isCompletion) {
      const header = document.getElementById('header-group');
      if (header) {
        this.#headerObserver = new ResizeObserver(this.#updateHeaderHeight);
        this.#headerObserver.observe(header);
      }
      this.#updateHeaderHeight();
    }

    this.#warnAboutMarkupDrift(steps);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#navigator?.removeEventListener(NAVIGATE_EVENT, this.#render);
    document.removeEventListener('solstice:rx:open', this.#handleOpenRequest);
    document.removeEventListener('keydown', this.#handleEscape);
    this.#restoreBackground();
    this.removeEventListener('change', this.#handleStepInput);
    this.refs.overlay?.removeEventListener('close', this.#handleOverlayClosed);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#variantGeneration++;
    this.#mobileSelectLabels?.removeEventListener('change', this.#updateResponsiveSelectLabels);
    this.#headerObserver?.disconnect();
  }

  /**
   * Use the compact dash for an unanswered select on mobile, and the clearer
   * word "Select" on larger screens. Only the option's label changes; its empty
   * value, and therefore validation and order data, remain exactly the same.
   */
  #updateResponsiveSelectLabels = () => {
    const labelKey = this.#mobileSelectLabels?.matches ? 'mobileLabel' : 'desktopLabel';

    for (const option of this.querySelectorAll('option[data-desktop-label][data-mobile-label]')) {
      option.textContent = option.dataset[labelKey] ?? '';
    }
  };

  /** Reveal the configurator. */
  open() {
    if (this.isCompletion) return;
    if (this.refs.overlay.open) return;
    if (this.refs.overlay.dataset.showHeader === 'true') {
      this.refs.overlay.show();
      this.#disableBackground();
    } else {
      this.refs.overlay.showModal();
    }
    this.#updateHeaderHeight();
    if (!this.#openedOnce) {
      this.#openedOnce = true;
      this.#emit('selector_opened');
    }
    this.#stepEnteredAt = Date.now();
    // Both modes lock page scrolling; header-visible mode leaves header controls active.
    document.body.style.overflow = 'hidden';
    this.#render();
  }

  /** Hide it again. Nothing the shopper has answered is discarded. */
  close() {
    // The camera is the one thing that must not survive the overlay going
    // away: a stream left running holds the device open and leaves its light
    // lit, which reads as the site still watching.
    this.closeCamera();
    this.refs.overlay.close();
  }

  /**
   * Move to the next step, if this one is satisfied.
   *
   * Clear lenses in a sunglasses frame are asked about first. The question now
   * goes in a dialog of our own rather than the browser's `confirm`, which
   * could only ever answer OK and Cancel — leaving the two wordings in theme
   * settings unused — and which stopped the page dead while it waited.
   *
   * So this hands over and returns. `confirmClearLenses` picks the journey
   * back up, and a shopper who presses Escape is left where they were with
   * their choice intact, which is the safest reading of a key meaning "not
   * now".
   */
  goForward() {
    if (this.#isAddingToCart || this.#completionSubmitted || this.#completionError) return;
    const { clearLensDialog } = this.refs;
    const needsAsking = this.#currentStepIs('lensColor') && needsClearLensConfirmation(this.#navigator.data);

    if (clearLensDialog && needsAsking) {
      clearLensDialog.showModal();
      return;
    }

    this.#advance();
  }

  /** The shopper stands by clear lenses, and carries on. */
  confirmClearLenses() {
    this.refs.clearLensDialog?.close();
    this.#advance();
  }

  /** They would rather pick a tint after all. */
  declineClearLenses() {
    this.refs.clearLensDialog?.close();

    // Emptying the choice makes the step visibly reset, rather than returning
    // them to the screen they just left with the same thing still selected.
    this.#navigator.setValue('lensColor', '');
  }

  /**
   * On to the next step, or into the bag if that was the last.
   *
   * Separate from `goForward` so the confirmation has something to hand back
   * to, and so nothing but `goForward` need know a confirmation exists.
   */
  #advance() {
    const leaving = this.#navigator.currentStep;
    const answersBefore = this.#navigator.data;
    const result = this.#navigator.goForward();
    if (result.moved) this.#emitStepCompleted(leaving, answersBefore);

    if (result.reason === 'invalid') {
      this.#showErrors(result.errors);
      return;
    }

    if (result.reason === 'atEnd') {
      if (this.isCompletion) this.#submitCompletion();
      else this.#addToCart();
    }
  }

  /**
   * Everything the order needs, from the steps the shopper actually walked.
   *
   * Public so it can be read from the console and held beside a real order
   * without anything having to be added to a bag to see it.
   *
   * @param {{ note?: string, attributes?: Object, items?: Array<Object> }} [cart]
   *   The cart as it stands, when one has been fetched. The note it carries is
   *   merged rather than replaced, and what it is already marked with decides
   *   whether this configuration should clear that mark.
   * @returns {{
   *   prescription: Record<string, string>,
   *   attributes: Record<string, string>,
   *   pricing: Object,
   *   request: { items: Array<Object>, note: string },
   * }}
   */
  buildOrder(cart = {}) {
    // Straight from the navigator rather than from the markup: a step that was
    // skipped has already had its keys left out, which is what keeps an
    // abandoned branch from following the shopper to checkout.
    const answers = this.#navigator.buildPayload();
    const pricing = this.#currentPricing();
    const prescription = buildPrescription(answers, this.#lookupWithLensColourInFull(answers), {
      prismPriceCents: pricing.prism,
    });

    return {
      prescription,
      attributes: buildCartAttributes(answers, cart),
      pricing,
      request: buildAddRequest({
        frame: {
          variantId: this.dataset.variantId,
          title: this.dataset.frameTitle ?? '',
          priceInCents: Number(this.dataset.framePriceRaw) || 0,
          discountTag: this.dataset.discountTag ?? '',
        },
        lensVariantId: this.dataset.lensVariantId,
        prescription,
        lensTotal: pricing.lensTotal,
        prescriptionMethod: answers.prescriptionMethod,
        opticianComment: answers.opticianComment,
        // Null unless the shopper uploaded or photographed one; the step that
        // collects it is skipped otherwise, so its key is simply absent.
        prescriptionFile: answers.prescriptionFile ?? null,
        prescriptionFileUrl: answers.prescriptionFileUrl ?? '',
        prescriptionFileId: answers.prescriptionFileId ?? '',
        linkId: this.#currentLinkId(),
        existingNote: cart.note ?? '',
      }),
    };
  }

  /** Move back a step. Never validates — a shopper may always retreat. */
  goBack() {
    if (this.#isAddingToCart || this.#completionSubmitted) return;
    this.#navigator.goBack();
  }

  /**
   * Open or close the selection panel, with the panel sliding rather than
   * snapping.
   *
   * A `<details>` shows and hides its contents outright, with no in-between
   * state for CSS to transition, so the toggle is taken over here: the click is
   * stopped, `open` is set at whichever end of the animation keeps the panel on
   * screen throughout — first when opening, last when closing — and the height
   * is animated between nothing and the panel's own measurement.
   *
   * @param {Event} event
   */
  toggleSummary(event) {
    const disclosure = this.refs.summaryDisclosure;
    const panel = this.refs.summaryList;
    if (!disclosure || !panel) return;

    event.preventDefault();

    // Where this click is heading: away from whatever the last one asked for,
    // which while an animation is still running is not the panel's own state.
    const shouldOpen = !(this.#summaryTargetOpen ?? disclosure.open);

    this.#summaryAnimation?.cancel();
    this.#summaryAnimation = null;
    this.#summaryTargetOpen = null;

    if (prefersReducedMotion()) {
      disclosure.open = shouldOpen;
      return;
    }

    // Opening has to come first, or there is nothing laid out to measure.
    if (shouldOpen) disclosure.open = true;

    this.#summaryTargetOpen = shouldOpen;
    this.#summaryAnimation = panel.animate(
      { height: ['0px', `${panel.scrollHeight}px`], opacity: [0, 1] },
      {
        duration: SUMMARY_ANIMATION_MS,
        easing: 'ease',
        // One set of keyframes covers both ways round.
        direction: shouldOpen ? 'normal' : 'reverse',
      }
    );

    this.#summaryAnimation.addEventListener('finish', () => {
      // Closing waits for the panel to finish shrinking, so it does not vanish
      // the instant the shopper clicks.
      if (!shouldOpen) disclosure.open = false;

      this.#summaryAnimation = null;
      this.#summaryTargetOpen = null;
    });
  }

  /**
   * Take up whatever a step arrives already showing, once, as it is reached.
   *
   * Two add-on groups name a default in admin, which Liquid renders as a
   * checked radio. Nothing else would tell the navigator: it hears about
   * choices through `change`, which only fires when the shopper picks something
   * themselves. Without this an option sits visibly selected while the step
   * refuses to advance, complaining that nothing has been chosen.
   *
   * On arrival rather than all at once at the start, so a default belonging to
   * a step the shopper has not seen yet stays out of the selection panel. That
   * is where the live configurator picks one up too.
   */
  #adoptCurrentStepValues() {
    const stepId = this.#navigator.currentStep.id;
    if (this.#adoptedSteps.has(stepId)) return;

    const stepElement = this.#currentStepElement();
    if (!stepElement) return;

    this.#adoptedSteps.add(stepId);

    // Recording values announces them, which would call `#render` back in on
    // top of the one already running. The flag makes that echo a no-op; the
    // render underneath carries on and draws the values we just recorded.
    this.#isAdopting = true;
    try {
      this.#navigator.setValues(readStepValues(stepElement));
    } finally {
      this.#isAdopting = false;
    }
  }

  /**
   * Context every analytics event carries, as the live theme sent it.
   * @returns {Record<string, unknown>}
   */
  #analyticsContext() {
    const data = this.#navigator?.data ?? {};
    const lensTypeInput = this.querySelector(
      `input[name="lensTypeHandle"][value="${CSS.escape(String(data.lensTypeHandle ?? ''))}"]`
    );
    const method = data.prescriptionMethod;
    return {
      session_id: this.#analyticsSessionId,
      product_id: Number(this.dataset.productId) || null,
      variant_id: Number(this.dataset.variantId) || null,
      sku: this.dataset.variantSku || null,
      brand: this.dataset.vendor || null,
      device_type: deviceType({ userAgent: navigator.userAgent, innerWidth: window.innerWidth }),
      lens_type: lensTypeInput?.dataset.optionTitle ?? null,
      prescription_method: method === 'upload' && this.#uploadSource === 'photo' ? 'camera' : method ?? null,
    };
  }

  /** @param {string} eventKey @param {Record<string, unknown>} [extra] */
  #emit(eventKey, extra = {}) {
    if (this.isCompletion) return;
    publishRxAnalytics(eventKey, this.#analyticsContext(), extra);
  }

  /**
   * `step_completed` for the step just left; after a prescription step also a
   * `pd` event saying how the pupillary distance was given.
   * @param {{ id: string } | undefined} step
   * @param {Record<string, unknown>} answers
   */
  #emitStepCompleted(step, answers) {
    const from = step && RX_ANALYTICS_STEPS[step.id];
    if (!from) return;
    const nextStep = this.#navigator.currentStep;
    const to = nextStep ? RX_ANALYTICS_STEPS[nextStep.id] : null;
    const titleOf = (field) => {
      const input = this.querySelector(
        `input[name="${field}"][value="${CSS.escape(String(answers[field] ?? ''))}"]`
      );
      return input?.dataset.optionTitle ?? null;
    };

    if (step.id === 'prescriptionManual' || step.id === 'prescriptionFile') {
      this.#emit('step_completed', {
        step_index: from.index,
        step_key: 'pd',
        selection: pupillaryDistanceSource(answers, this.#pdMeasuredWithVeero),
        time_on_step_ms: null,
        next_step_index: to?.index ?? null,
        next_step_key: to?.key ?? null,
      });
    }

    this.#emit('step_completed', {
      step_index: from.index,
      step_key: from.key,
      selection: stepSelection(step.id, answers, titleOf, { uploadSource: this.#uploadSource }),
      time_on_step_ms: this.#stepEnteredAt ? Date.now() - this.#stepEnteredAt : null,
      next_step_index: to?.index ?? null,
      next_step_key: to?.key ?? null,
    });
    this.#stepEnteredAt = Date.now();
  }

  /**
   * The frame facts that depend on the variant, read from the configurator in
   * the product-select response. Answers already given are kept.
   */
  static VARIANT_KEYS = [
    'variantId',
    'variantSku',
    'frameIsSunglasses',
    'framePrice',
    'framePriceAmount',
    'framePriceRaw',
    'frameCartOffer',
    'discountTag',
    'freeRxApplies',
  ];

  #handleProductSelect = (event) => {
    if (this.isCompletion) return;
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#variantGeneration;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html || !this.isConnected || generation !== this.#variantGeneration) return;
        const next = detail.html.querySelector(`solstice-rx-configurator[data-product-id="${this.dataset.productId}"]`);
        if (!(next instanceof HTMLElement)) return;
        for (const key of RxConfigurator.VARIANT_KEYS) {
          if (next.dataset[key] !== undefined) this.dataset[key] = next.dataset[key];
        }
        for (const selector of ['[data-rx-frame-image]', '[data-rx-frame-price]', '[data-rx-total-offer]']) {
          const current = this.querySelector(selector);
          const replacement = next.querySelector(selector);
          if (current && replacement) current.innerHTML = replacement.innerHTML;
        }
        this.#navigator?.setValues({ frameIsSunglasses: this.dataset.frameIsSunglasses === 'true' });
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[solstice-rx-configurator] Event promise rejected:', error);
      });
  };

  #handleOpenRequest = (event) => {
    // Several products could each render a configurator; only the one the
    // launcher named should answer.
    const requestedProductId = event.detail?.productId;
    if (requestedProductId && String(requestedProductId) !== this.dataset.productId) return;

    // The launcher follows the selected variant; the cart line must too.
    const requestedVariantId = event.detail?.variantId;
    if (requestedVariantId) this.dataset.variantId = String(requestedVariantId);

    this.open();
  };

  #handleOverlayClosed = () => {
    if (!this.refs.overlay.open) {
      document.body.style.overflow = '';
      this.#restoreBackground();
    }
  };

  #handleStepInput = (event) => {
    if (this.#completionSubmitted || this.#isAddingToCart) return;
    const changedInput = event.target;
    if (!(changedInput instanceof Element)) return;

    const stepElement = changedInput.closest('[data-step-id]');
    if (!stepElement) return;

    // Acted on before the form is read, because a section the shopper has just
    // switched off has to stop contributing in this same pass rather than the
    // next one. The step's own answers are laid over everything known so far,
    // since a condition may turn on the lens type chosen two steps earlier.
    const blanked = this.#updateConditionalFields({
      ...this.#navigator.data,
      ...readStepValues(stepElement),
    });

    // The blanked fields are laid over the top because they have just been
    // disabled, and a disabled control is not in what the form reads back.
    this.#navigator.setValues({ ...readStepValues(stepElement), ...blanked });
    this.#dropChoicesRuledOutBy(changedInput);

    if (this.#optionSelectionShouldAdvance(changedInput)) this.goForward();
  };

  /**
   * Whether this change completed one of the large option-card steps.
   *
   * Defaults adopted when a step opens never produce a change event, so they
   * cannot skip a screen. Detailed controls use different classes and stay on
   * their screen until Continue is pressed.
   *
   * @param {EventTarget} changedInput
   * @returns {boolean}
   */
  #optionSelectionShouldAdvance(changedInput) {
    if (!(changedInput instanceof HTMLInputElement)) return false;
    if (!changedInput.matches('.rx-option__input[type="radio"]')) return false;

    if (changedInput.name !== 'lensColor') {
      return shouldAutoAdvanceOption(changedInput.name);
    }

    const shadeGroup = Array.from(this.querySelectorAll('[data-shade-group]')).find(
      (group) => group.dataset.shownWhen === `lensColorIs:${changedInput.value}`
    );

    return shouldAutoAdvanceOption(changedInput.name, {
      subOptionCount: shadeGroup?.querySelectorAll('input[name="lensColorShade"]').length ?? 0,
      hasAdjustableSubOption: Boolean(
        shadeGroup?.querySelector('input[name="tintDarknessPercent"]')
      ),
    });
  }

  /**
   * Switch on and off the parts of a step that depend on what has been answered
   * inside it: the second eye, the per-eye pupillary distance, the prism table,
   * and the addition that only some lens types are ground with.
   *
   * Hiding alone would not do. A field hidden with CSS is still submitted, so a
   * left eye the shopper has switched off would go on contributing values that
   * nothing validates — which is how the old configurator carried a
   * prescription past the screen that collected it. Disabling the controls is
   * what actually takes them out of `readStepValues`, while the markup keeps
   * what was typed in case the shopper switches the section back on.
   *
   * @param {Record<string, unknown>} answers
   */
  #updateConditionalFields(answers) {
    const conditions = {
      ...answers,
      // Not something the shopper answers: it follows from the lens type they
      // chose two steps back, and the step list is what decides which types
      // need it.
      requiresAddPower: LENS_TYPES[answers.lensTypeHandle]?.requiresAddPower === true,
    };

    // One condition per lens colour, so a shade picker can be tied to the
    // colour it belongs to with the same `data-shown-when` that every other
    // conditional section already uses.
    for (const colour of this.querySelectorAll('input[name="lensColor"]')) {
      conditions[`lensColorIs:${colour.value}`] = answers.lensColor === colour.value;
    }

    for (const section of this.querySelectorAll('[data-shown-when]')) {
      this.#setSectionActive(section, Boolean(conditions[section.dataset.shownWhen]));
    }

    for (const section of this.querySelectorAll('[data-hidden-when]')) {
      this.#setSectionActive(section, !conditions[section.dataset.hiddenWhen]);
    }

    // Both last, because switching a section on above has just enabled
    // everything inside it — base directions and swatches alike.
    return { ...this.#updateDependentControls(conditions), ...this.#chooseShadeIfNoneIs() };
  }

  /**
   * Start a colour that comes in shades on the first of them.
   *
   * Which is what the live store records: a shopper who never opens the
   * swatches still orders "Polarized - Grey", not a bare "Polarized". It
   * cannot be done in the markup, because every swatch across every colour
   * shares one field name — they are a single radio group however many colours
   * they are spread across, and a browser keeps only the last `checked` among
   * them.
   *
   * Switching colours therefore forgets the shade picked under the old one,
   * and the new colour starts at its own first. The live store remembers per
   * colour; this does not, and lands on the same answer for anyone who has not
   * gone back and forth.
   *
   * @returns {Record<string, string>} the shade chosen for them, to be recorded
   */
  #chooseShadeIfNoneIs() {
    const group = this.querySelector('[data-shade-group]:not([hidden])');
    if (!group) return {};

    const shades = group.querySelectorAll('input[name="lensColorShade"]');
    if (shades.length === 0) return {};

    const chosen = Array.from(shades).find((shade) => shade.checked) ?? shades[0];
    chosen.checked = true;

    const label = group.querySelector('[data-shade-label]');
    if (label) label.textContent = chosen.value;

    return { lensColorShade: chosen.value };
  }

  /**
   * Which face the upload step is showing.
   *
   * Five of them: `waiting` for a file, `camera` with the preview open,
   * `sending` while it goes up, `done` once it has, `failed` if it did not.
   * Everything the shopper sees on that step follows this one word.
   *
   * @param {'waiting' | 'camera' | 'sending' | 'done' | 'failed'} state
   */
  #setUploadState(state) {
    const step = this.refs.steps.find((element) => element.dataset.stepId === 'prescriptionFile');
    if (!step) return;

    step.dataset.uploadState = state;

    // Tied to the state rather than to each path out of an upload, so a bar
    // cannot be left animating behind a panel nobody is looking at.
    this.#showUploadProgress(state === 'sending');

    for (const part of step.querySelectorAll('[data-when-state]')) {
      const showsNow = part.dataset.whenState.split(' ').includes(state);
      part.hidden = !showsNow;

      // A file input inside a panel nobody can see must not be reachable by
      // keyboard, and must not be submitted with the step.
      for (const control of part.querySelectorAll('input, button, select, textarea')) {
        control.disabled = !showsNow;
      }
    }
  }

  /**
   * Put the file's name in every panel that shows one.
   *
   * Three of them do, and which is on screen depends on how the upload went.
   * Writing to all of them is cheaper than working out which.
   *
   * @param {string} filename
   */
  #showUploadFilename(filename) {
    for (const slot of this.querySelectorAll('[data-upload-filename]')) {
      slot.textContent = filename;
    }
  }

  /**
   * Start or stop the bar that says an upload is under way.
   *
   * It does not say *how far*, and cannot. Asking the browser how many bytes
   * have gone means registering a listener on the request's upload object, and
   * that alone turns a simple cross-origin request into one that is
   * preflighted — which the endpoint has no way to answer. See
   * `#postPrescriptionFile`.
   *
   * So the bar travels rather than fills, and carries no `aria-valuenow`,
   * which is how a progress bar says it does not know.
   *
   * @param {boolean} isRunning
   */
  #showUploadProgress(isRunning) {
    const { uploadBar } = this.refs;
    if (!uploadBar) return;

    uploadBar.classList.toggle('rx-upload__bar--running', isRunning);
    uploadBar.removeAttribute('aria-valuenow');
  }

  /**
   * @param {string} message What went wrong, in one line.
   * @param {string} [hint] What the shopper might do about it.
   */
  #showUploadFailure(message, hint = '') {
    const { uploadFailure, uploadHint } = this.refs;

    if (uploadFailure) uploadFailure.textContent = message;
    if (uploadHint) uploadHint.textContent = hint;

    this.#setUploadState('failed');
  }

  /**
   * Take in the shopper's prescription file, and either hold it or send it on
   * depending on which flow this is.
   *
   * Buying a frame: the file is held rather than sent. It travels later as a
   * native Shopify line-item property, attached to the `/cart/add.js` request
   * itself — see `#addToCart`. The completion page has no cart to attach a
   * property to, so it alone still uploads through `#sendPrescription`, to
   * the address in theme settings.
   *
   * @param {Event} event
   */
  async uploadPrescription(event) {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';

    const problem = describeUploadProblem(file);
    if (problem) {
      this.#showUploadFilename(file?.name ?? '');
      this.#showUploadFailure(problem, uploadRetryHint());
      return;
    }

    this.#uploadSource = 'file';
    if (this.usesUploadEndpoint) await this.#sendPrescription(file);
    else this.#selectPrescription(file);
  }

  /**
   * Hold a chosen or photographed prescription for the native line-item file
   * property to carry, instead of uploading it anywhere now.
   *
   * @param {File} file
   */
  #selectPrescription(file) {
    this.#showUploadFilename(buildUploadFilename(this.#currentLinkId(), file.name));
    this.#navigator.setValues({ prescriptionFile: file, prescriptionFileUrl: '', prescriptionFileId: '' });
    this.#setUploadState('done');
  }

  /**
   * Send the shopper's prescription off to be filed, and remember where it
   * went.
   *
   * Used by completion and the Google Script product upload mode.
   * The endpoint returns the reference carried by the order.
   *
   * @param {File} file
   */
  async #sendPrescription(file) {
    this.#uploadController?.abort();
    this.#uploadRequest?.abort();
    const generation = ++this.#uploadGeneration;
    const controller = new AbortController();
    this.#uploadController = controller;
    this.#uploadRequest = null;
    this.#navigator.setValues({ prescriptionFile: null, prescriptionFileUrl: '', prescriptionFileId: '' });
    const endpoint = this.dataset.uploadEndpoint;
    if (!endpoint) {
      console.error('[solstice-rx-configurator] No upload address is configured.');
      this.#showUploadFailure(
        'We cannot take a file just now.',
        'Go back and enter your prescription by hand instead.'
      );
      return;
    }

    const filename = buildUploadFilename(this.#currentLinkId(), file.name);

    this.#showUploadFilename(filename);
    this.#setUploadState('sending');

    try {
      const data = await readFileAsBase64(file, { signal: controller.signal });
      if (generation !== this.#uploadGeneration || controller.signal.aborted) return;
      const answer = await this.#postPrescriptionFile({
        endpoint,
        filename,
        mimeType: file.type,
        data,
      });
      if (generation !== this.#uploadGeneration || controller.signal.aborted) return;

      const { url, fileId } = readUploadResult(answer);

      this.#navigator.setValues({ prescriptionFileUrl: url, prescriptionFileId: fileId });
      this.#setUploadState('done');
    } catch (error) {
      if (generation !== this.#uploadGeneration || controller.signal.aborted) return;
      console.error('[solstice-rx-configurator] Upload failed:', error);
      this.#showUploadFailure('Upload failed. Please try again.', uploadRetryHint());
    } finally {
      if (generation === this.#uploadGeneration) {
        this.#uploadController = null;
        this.#uploadRequest = null;
      }
    }
  }

  /**
   * The upload request itself.
   *
   * Two details keep this working, and both are easy to undo by accident.
   *
   * It is sent as `text/plain`. That keeps it a *simple* cross-origin request,
   * which the browser sends straight out. Any other content type makes the
   * browser ask permission first, with an OPTIONS request the endpoint has no
   * way to answer — an Apps Script web app can only be a `doGet` and a
   * `doPost`, and cannot set the headers a preflight needs. The upload then
   * fails before it has left the browser.
   *
   * And **nothing may listen to `request.upload`**. Registering even one
   * listener there provokes the same preflight, whatever the content type, so
   * asking the browser how many bytes have gone costs the upload itself. That
   * is the whole reason the bar travels instead of filling; it was written the
   * other way first and broke exactly this.
   *
   * `XMLHttpRequest` rather than `fetch` only for `timeout`, which is one
   * property here against an AbortController and a stray timer there.
   *
   * @param {{ endpoint: string, filename: string, mimeType: string, data: string }} body
   * @returns {Promise<string>} The response text, for `readUploadResult`.
   */
  #postPrescriptionFile({ endpoint, filename, mimeType, data }) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();

      request.open('POST', endpoint, true);
      request.timeout = UPLOAD_TIMEOUT_MS;
      request.setRequestHeader('Content-Type', 'text/plain;charset=utf-8');

      request.addEventListener('load', () => resolve(request.responseText));
      request.addEventListener('error', () => reject(new Error('The upload could not be sent.')));
      request.addEventListener('timeout', () => reject(new Error('The upload took too long.')));
      request.addEventListener('abort', () => reject(new Error('The upload was stopped.')));

      this.#uploadRequest = request;
      request.send(JSON.stringify({ filename, mimeType, data }));
    });
  }

  /**
   * Open the camera and show what it sees.
   *
   * A file input marked `capture` was doing this job, and only ever worked on
   * a phone — a desktop browser ignores the attribute and opens a file browser
   * instead. This asks for the camera itself, so both behave the same.
   */
  async openCamera() {
    this.#setUploadState('camera');

    const { cameraPreview, cameraPlaceholder } = this.refs;
    if (!cameraPreview) return;

    try {
      // The back camera on a phone, where the prescription will be. A laptop
      // has only one and gives it whatever is asked for.
      this.#cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      cameraPreview.srcObject = this.#cameraStream;
      await cameraPreview.play();

      if (cameraPlaceholder) cameraPlaceholder.hidden = true;
    } catch (error) {
      console.error('[solstice-rx-configurator] The camera could not be opened:', error);

      const { message, hint } = describeCameraProblem(error, await this.#cameraPermission());

      this.closeCamera();
      this.#showUploadFailure(message, hint);
    }
  }

  /**
   * What the browser has on file about the camera for this site.
   *
   * It separates the two refusals that look identical from `getUserMedia`: one
   * the shopper dismissed, which asking again will re-raise, and one they
   * blocked, which will not be asked again at all. Only the second is worth
   * sending them to the address bar for.
   *
   * Safari does not answer for the camera and throws instead, so a browser
   * that will not say is read as not having refused.
   *
   * @returns {Promise<'granted' | 'denied' | 'prompt'>}
   */
  async #cameraPermission() {
    try {
      const status = await navigator.permissions.query({ name: 'camera' });
      return status.state;
    } catch {
      return 'prompt';
    }
  }

  /**
   * Take the picture and send it.
   *
   * The frame is drawn to a canvas at whatever size the camera gave, then
   * asked for as a JPEG — a prescription is text on white, which a photograph
   * of any quality renders legibly and which PNG would make several times
   * larger for nothing.
   */
  takePicture() {
    const { cameraPreview } = this.refs;
    if (!cameraPreview?.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = cameraPreview.videoWidth;
    canvas.height = cameraPreview.videoHeight;
    canvas.getContext('2d').drawImage(cameraPreview, 0, 0);

    canvas.toBlob(
      (picture) => {
        if (!picture) {
          this.#showUploadFailure('That picture could not be taken.', uploadRetryHint());
          return;
        }

        const photograph = new File([picture], 'prescription-photo.jpg', { type: 'image/jpeg' });

        this.closeCamera();
        this.#uploadSource = 'photo';
        if (this.usesUploadEndpoint) this.#sendPrescription(photograph);
        else this.#selectPrescription(photograph);
      },
      'image/jpeg',
      CAMERA_PICTURE_QUALITY
    );
  }

  /**
   * Put the camera away.
   *
   * Every track has to be stopped by hand. A stream left running holds the
   * camera open and leaves the light on it lit, which reads as the site
   * watching after the shopper has finished with it.
   */
  closeCamera() {
    for (const track of this.#cameraStream?.getTracks() ?? []) {
      track.stop();
    }
    this.#cameraStream = null;

    const { cameraPreview, cameraPlaceholder } = this.refs;
    if (cameraPreview) cameraPreview.srcObject = null;
    if (cameraPlaceholder) cameraPlaceholder.hidden = false;

    if (this.#uploadStateNow() === 'camera') this.#setUploadState('waiting');
  }

  /**
   * Let Veero measure the combined PD for the form containing this button.
   * The result is applied through a bubbling change event, so it follows the
   * same navigator and validation path as a value chosen by hand.
   *
   * @param {Event} event
   */
  measurePupillaryDistance(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const group = button.closest('.rx-pupillary-distance');
    const select = group?.querySelector('select[name="pupillaryDistance"]');
    const errorMessage = group?.querySelector('[data-rx-measure-pd-error]');
    if (!(select instanceof HTMLSelectElement)) return;

    if (errorMessage) {
      errorMessage.textContent = '';
      errorMessage.hidden = true;
    }

    // Veero gets its own top-layer dialog; RX remains open underneath.
    const veeroRoot = document.querySelector('#veero-eyefit');
    const restoreVeero = presentVeeroOverRx(veeroRoot);

    let veeroObserver = null;
    let hasSeenVeeroModal = false;
    let hasFinishedMeasurementUi = false;
    const finishMeasurementUi = () => {
      if (hasFinishedMeasurementUi) return;
      hasFinishedMeasurementUi = true;
      veeroObserver?.disconnect();
      button.disabled = false;
      button.removeAttribute('aria-busy');
      // Let the vendor finish removing its UI before restoring its mount.
      window.setTimeout(() => {
        restoreVeero();
        if (this.refs.overlay.open) document.body.style.overflow = 'hidden';
        if (button.isConnected) button.focus({ preventScroll: true });
      }, 0);
    };

    // Veero's close button on its first screen does not call the configured
    // exit callback. Watching that modal become hidden gives cancellation the
    // same restoration path as a result, an error, or a later exit.
    if (veeroRoot) {
      veeroObserver = new MutationObserver(() => {
        const modal = veeroRoot.querySelector('.veero-eyefit-modal');
        const modalIsVisible = modal && getComputedStyle(modal).display !== 'none';

        if (modalIsVisible) {
          hasSeenVeeroModal = true;
        } else if (hasSeenVeeroModal) {
          finishMeasurementUi();
        }
      });
      veeroObserver.observe(veeroRoot, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
        childList: true,
        subtree: true,
      });
    }

    measureVeeroPupillaryDistance({
      eyeSize: window.EyeSize,
      vendorId: window.SOLSTICE_VEERO_VENDOR_ID,
      select,
      onStart() {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      },
      onFinish: finishMeasurementUi,
      onMeasured: () => {
        this.#pdMeasuredWithVeero = true;
      },
      onError(error) {
        console.error('[solstice-rx-configurator] Veero PD measurement failed:', error);
        if (!errorMessage) return;

        errorMessage.textContent =
          "We couldn't use that measurement. Please try again or choose your PD from the list.";
        errorMessage.hidden = false;
      },
    });
  }

  /**
   * Throw away an uploaded prescription so another can be chosen.
   *
   * The file itself is already filed and stays where it is — this only stops
   * the order pointing at it. Deleting would need a second endpoint that does
   * not exist, and a shopper midway through should not wait on one.
   */
  discardUpload() {
    if (this.usesUploadEndpoint) {
      this.#uploadGeneration++;
      this.#uploadController?.abort();
      this.#uploadController = null;
      this.#uploadRequest?.abort();
      this.#uploadRequest = null;
      this.#navigator.setValues({ prescriptionFile: null, prescriptionFileUrl: '', prescriptionFileId: '' });
    } else {
      this.#navigator.setValues({ prescriptionFile: null });
    }

    this.#showUploadFilename('');
    this.#setUploadState('waiting');
  }

  /** @returns {string} */
  #uploadStateNow() {
    const step = this.refs.steps.find((element) => element.dataset.stepId === 'prescriptionFile');
    return step?.dataset.uploadState ?? 'waiting';
  }

  /**
   * The handle this configuration is known by, made on first use.
   *
   * The uploaded file is named after it and so is the order, which is what
   * lets a prescription that arrives on its own be matched to the order
   * waiting for it.
   *
   * @returns {string}
   */
  #currentLinkId() {
    this.#linkId ??= generateLinkId();
    return this.#linkId;
  }

  /**
   * The option lookup with the lens colour written out in full.
   *
   * `buildPrescription` names every add-on by looking its handle up here, and
   * for the colour the order wants more than the colour's own title — it wants
   * the shade, and a tint's darkness. Composing it into the lookup on the way
   * past keeps the serialiser free of anything it would have to know about
   * colours, and keeps one function deciding the wording for both the panel
   * and the order.
   *
   * @param {Record<string, unknown>} answers
   * @returns {Map<string, { title: string, price: number }>}
   */
  #lookupWithLensColourInFull(answers) {
    const key = `lensColor:${answers.lensColor}`;
    const colour = this.#optionLookup.get(key);
    if (!colour) return this.#optionLookup;

    return new Map(this.#optionLookup).set(key, {
      ...colour,
      title: describeLensColor(colour.title, answers),
    });
  }

  /** Keep the darkness reading beside the slider in step with it. */
  #updateDarknessOutput() {
    const slider = this.querySelector('input[name="tintDarknessPercent"]');
    const output = this.querySelector('[data-darkness-output]');
    if (!slider || !output) return;

    output.textContent = `${slider.value}%`;
    for (const preset of this.querySelectorAll('[data-darkness-preset]')) {
      preset.setAttribute('aria-pressed', String(preset.dataset.darknessPreset === slider.value));
    }
  }

  /**
   * Set a tint's darkness from one of the named presets.
   *
   * @param {Event} event
   */
  chooseDarkness(event) {
    const slider = this.querySelector('input[name="tintDarknessPercent"]');
    const preset = event.target.closest('[data-darkness-preset]');
    if (!slider || !preset) return;

    slider.value = preset.dataset.darknessPreset;
    slider.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Hold a control shut until the one it belongs to has been answered — which
   * today means a prism's base direction, meaningless until there is a prism
   * strength for it to describe.
   *
   * Any value it was holding is put back to blank on the way, and the names of
   * the ones blanked are handed back. A disabled control is left out of
   * `readStepValues` altogether, so without that the caller would go on
   * believing in a base direction pointing at a strength the shopper has since
   * taken away — and the step would refuse to advance over a field no longer
   * on screen to correct.
   *
   * @param {Record<string, unknown>} conditions
   * @returns {Record<string, string>} the fields blanked, to be recorded
   */
  #updateDependentControls(conditions) {
    /** @type {Record<string, string>} */
    const blanked = {};

    for (const control of this.querySelectorAll('[data-enabled-by]')) {
      const answer = conditions[control.dataset.enabledBy];
      const isAnswered = answer != null && answer !== '' && answer !== PRISM_BLANK;

      if (!isAnswered && control.value !== PRISM_BLANK) {
        control.value = PRISM_BLANK;
        blanked[control.name] = PRISM_BLANK;
      }

      control.disabled = !isAnswered;
    }

    return blanked;
  }

  /**
   * @param {HTMLElement} section
   * @param {boolean} isActive
   */
  #setSectionActive(section, isActive) {
    section.hidden = !isActive;

    for (const control of section.querySelectorAll('input, select, textarea')) {
      control.disabled = !isActive;
    }
  }

  #renderedStepId = null;

  #render = () => {
    if (this.#isAdopting) return;

    const currentStepId = this.#navigator.currentStep.id;
    if (currentStepId !== this.#renderedStepId) {
      this.querySelector('.rx-overlay__scroll')?.scrollTo({ top: 0 });
      this.#renderedStepId = currentStepId;
    }

    for (const stepElement of this.refs.steps) {
      stepElement.hidden = stepElement.dataset.stepId !== currentStepId;
    }

    // Sections first, so the step is in the right shape before its markup is
    // read, and then the defaults, so the panel and the prices below see them
    // the moment their step comes up.
    this.#updateConditionalFields(this.#navigator.data);
    this.#adoptCurrentStepValues();
    this.#clearMessages();
    this.#updateAvailability();
    this.#updateOptionPrices();
    this.#updateProgress();
    // Re-applying whichever face the upload step is already showing. The
    // markup ships with the right things hidden but not with the right things
    // disabled, and a control inside a panel nobody can see must not be
    // reachable by keyboard. Idempotent, so it never moves the state itself.
    this.#setUploadState(this.#uploadStateNow());
    this.#updateDarknessOutput();
    this.#updateSummary();
    this.#updateReview();
    this.#updateTotal();
  };

  /**
   * Read the add-ons that cannot be bought together out of the markup.
   *
   * A pair naming an option that has since been deleted arrives with a blank
   * handle. It is dropped with a warning rather than throwing — a merchandiser
   * tidying up in admin should never be able to break the configurator.
   */
  #readIncompatibleOptions() {
    return Array.from(this.querySelectorAll('[data-incompatible-pair]'))
      .map((element) => ({
        first: element.dataset.first,
        second: element.dataset.second,
        firstTitle: element.dataset.firstTitle,
        secondTitle: element.dataset.secondTitle,
        message: element.dataset.message,
      }))
      .filter((pair) => {
        if (pair.first && pair.second) return true;

        console.warn(
          '[solstice-rx-configurator] Ignoring an incompatible pair that names a missing option.'
        );
        return false;
      });
  }

  /**
   * Grey out the options that the shopper's existing choices rule out, so a bad
   * combination cannot be reached in the first place.
   *
   * This is a courtesy, not the rule. The validators refuse the same
   * combinations whatever the markup says.
   */
  #updateOptionPrices() {
    const chosen = this.#chosenOptions();
    for (const input of this.querySelectorAll('input[data-option-price]')) {
      const option = this.#optionLookup.get(`${input.name}:${input.value}`);
      const label = input.closest('.rx-option')?.querySelector('[data-rx-option-price]');
      if (!option || !label) continue;
      const titles = new Set(chosen.filter((item) => item.field !== input.name).map((item) => item.title));
      titles.add(option.title);
      const price = priceOfOption(option, titles, this.dataset.freeRxApplies === 'true');
      const originalPrice = priceOfOption(option, titles, false);
      label.querySelector('[data-rx-resolved-price]').textContent = this.#formatMoney(price);
      const original = label.querySelector('[data-rx-original-price]');
      original.textContent = this.#formatMoney(originalPrice);
      original.hidden = originalPrice <= price;
    }
  }

  #updateAvailability() {
    if (this.#incompatibleOptions.length === 0) return;

    const data = this.#navigator.data;

    for (const field of ADD_ON_FIELDS) {
      for (const input of this.querySelectorAll(`input[name="${field}"]`)) {
        const clash = findIncompatibility(input.value, field, data, this.#incompatibleOptions);

        input.disabled = Boolean(clash);
        input.closest('.rx-option')?.classList.toggle('rx-option--unavailable', Boolean(clash));
      }
    }
  }

  /**
   * When a new choice rules out one made earlier, drop the earlier one rather
   * than leaving it to fail validation further along. The shopper sees the
   * selection disappear, which is plainer than an error about a screen they
   * have already left.
   *
   * @param {HTMLInputElement} changedInput
   */
  #dropChoicesRuledOutBy(changedInput) {
    if (!ADD_ON_FIELDS.includes(changedInput.name)) return;

    const data = this.#navigator.data;
    const dropped = {};

    for (const field of ADD_ON_FIELDS) {
      if (field === changedInput.name) continue;
      if (!findIncompatibility(data[field], field, data, this.#incompatibleOptions)) continue;

      dropped[field] = '';
      for (const input of this.querySelectorAll(`input[name="${field}"]`)) {
        input.checked = false;
      }
    }

    if (Object.keys(dropped).length > 0) this.#navigator.setValues(dropped);
  }

  /**
   * Fill the rule under the step title to show how far along the shopper is.
   *
   * Every step carries its own bar, all showing the same figure, so the value
   * is written to all of them rather than to the visible one — nothing here has
   * to know which step is on screen.
   */
  #updateProgress() {
    const navigator = this.#navigator;
    const position = navigator.currentStepIndex + 1;
    const total = navigator.totalSteps;

    const reached = position / total;

    for (const bar of this.querySelectorAll('[data-step-progress]')) {
      // The number of steps depends on what the shopper has chosen, so the
      // maximum cannot be written into the markup.
      bar.setAttribute('aria-valuemin', '1');
      bar.setAttribute('aria-valuemax', String(total));
      bar.setAttribute('aria-valuenow', String(position));
      bar.setAttribute('aria-valuetext', `Step ${position} of ${total}`);

      const fill = bar.querySelector('[data-step-progress-fill]');
      if (!fill) continue;

      // The bar the shopper is about to look at was hidden a moment ago, and a
      // transition will not run from a width the browser never drew. So it is
      // first put back where the previous step left off and a layout property
      // read, which forces that starting width to be committed; only then does
      // it grow. Done for the one bar on screen, since a forced layout per
      // hidden step would be nine of them for nothing.
      if (bar.closest('[data-step-id]')?.hidden === false) {
        fill.style.width = `${this.#shownProgress * 100}%`;
        void fill.offsetWidth;
      }

      fill.style.width = `${reached * 100}%`;
    }

    this.#shownProgress = reached;

    if (this.refs.backButton) {
      this.refs.backButton.hidden = navigator.isFirstStep;
    }

    this.refs.continueButton.textContent = navigator.isLastStep && (!this.isCompletion || navigator.currentStep.id !== 'prescriptionMethod')
      ? (this.isCompletion ? 'Submit prescription' : 'Add to bag') : 'Continue';
    if (this.#isAddingToCart) this.#setContinueBusy(true);
    if (this.isCompletion) {
      this.refs.continueButton.disabled = Boolean(this.#completionError || this.#completionSubmitted || this.#isAddingToCart);
      if (this.#completionError) this.#showMessage(this.#completionError);
    }
  }

  /**
   * What every option is called, what it costs, and the two things that can
   * change what it costs — taken once from the rendered markup, which Liquid
   * built from the metaobjects, so there is still only one source for all of it.
   *
   * Keyed by field as well as handle, so two groups offering the same option
   * can never be confused for one another.
   *
   * @returns {Map<string, import('@theme/solstice-rx-pricing').PricedOption>}
   */
  #readOptionLookup() {
    const lookup = new Map();

    for (const input of this.querySelectorAll('input[data-option-title]')) {
      lookup.set(`${input.name}:${input.value}`, {
        title: input.dataset.optionTitle,
        // An option with no price attribute is chosen but not bought, and the
        // difference survives: null here, an empty cell in the panel, whereas
        // nought would read as "free".
        price: input.dataset.optionPrice == null ? null : Number(input.dataset.optionPrice) || 0,
        dynamicPrices: readDynamicPrices(input),
        freeOnThreshold: input.hasAttribute('data-free-on-threshold'),
      });
    }

    return lookup;
  }

  /**
   * Everything chosen so far, on the steps that still apply.
   *
   * Walks the same `summaryField` the selection panel walks, so the panel and
   * the total can never come to disagree about what has been chosen.
   *
   * @returns {import('@theme/solstice-rx-pricing').PricedOption[]}
   */
  #chosenOptions() {
    const data = this.#navigator.data;
    const chosen = [];

    for (const step of this.#navigator.activeSteps) {
      if (!step.summaryField) continue;

      const option = this.#optionLookup.get(`${step.summaryField}:${data[step.summaryField]}`);
      if (option) chosen.push({ field: step.summaryField, ...option });
    }

    return chosen;
  }

  /** What the shopper is being asked to pay, as it stands. */
  #currentPricing() {
    return buildPricing({
      chosen: this.#chosenOptions(),
      framePrice: Number(this.dataset.framePriceAmount) || 0,
      freeRxApplies: this.dataset.freeRxApplies === 'true',
      includesPrism: Boolean(this.#navigator.buildPayload().includesPrism),
      prismPrice: Number(this.dataset.prismPrice) || 0,
    });
  }

  #updateTotal() {
    if (!this.refs.total) return;

    this.refs.total.textContent = this.#formatMoney(this.#currentPricing().total);
  }

  /** @param {number} amount - Shopify's hundredths, including zero-decimal currencies. */
  #formatMoney(amount) {
    const currency = this.dataset.currency || 'USD';
    const precision = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    const minorUnits = Math.round(amount * (10 ** precision) / 100);
    return formatMoney(minorUnits, this.dataset.moneyFormat || '{{amount}}', currency);
  }

  /**
   * The "Your selection" panel, derived rather than maintained.
   *
   * Everything below the frame comes from the steps that currently apply, so
   * the panel inherits every skip rule without being told any of them twice.
   */
  #updateSummary() {
    const summaryList = this.refs.summaryList;
    if (!summaryList) return;

    const rows = [
      {
        label: 'Frame',
        value: this.dataset.frameTitle ?? '',
        price: Number(this.dataset.framePriceAmount) || 0,
        offer: this.dataset.frameCartOffer || '',
      },
      ...this.#chosenSummaryRows(this.#currentPricing()),
    ];

    this.#renderRows(summaryList, rows, 'rx-summary__price');
  }

  /**
   * What the shopper has chosen, priced.
   *
   * Every row is charged at what it is actually being charged, which is not
   * always what the option costs on its own — a free-lens offer or another
   * choice can move it, and only the pricing knows that. Taking the figures
   * from there is what keeps the rows adding up to the total beneath them.
   *
   * @param {ReturnType<typeof buildPricing>} pricing
   */
  #chosenSummaryRows(pricing) {
    const charged = new Map(pricing.lines.map((line) => [line.field, line.price]));

    const rows = buildSelectionSummary(
      this.#navigator.data,
      this.#navigator.activeSteps,
      this.#optionLookup
    ).map((row) => ({ ...row, price: charged.get(row.field) }));

    // Prism is charged flat and belongs to no option, so no step produces a row
    // for it. Without this one the rows come to exactly the prism charge less
    // than the total underneath them.
    if (pricing.prism > 0) {
      rows.push({ label: 'Prism', value: 'Selected', price: pricing.prism });
    }

    return rows;
  }

  /**
   * Fill a three-column list with a label, a value and a price.
   *
   * @param {HTMLElement} list
   * @param {Array<{ label: string, value: string, price?: number | null, offer?: string }>} rows
   * @param {string} [priceClassName]
   */
  #renderRows(list, rows, priceClassName = '') {
    list.replaceChildren();

    for (const row of rows) {
      const term = document.createElement('dt');
      term.textContent = row.label;

      const detail = document.createElement('dd');
      // An em dash reads better than an empty cell for something not yet chosen.
      detail.textContent = row.value === '' ? '—' : row.value;

      const price = document.createElement('dd');
      if (priceClassName) price.className = priceClassName;
      // Nothing chosen, nothing to charge — an empty cell rather than a zero,
      // which would read as "free" instead of "not yet".
      price.textContent = row.price == null ? '' : this.#formatMoney(row.price);
      if (row.offer) {
        const offer = document.createElement('span');
        offer.className = 'solstice__cart-offer';
        offer.textContent = row.offer;
        price.append(offer);
      }

      const group = document.createElement('div');
      group.className = priceClassName ? 'rx-summary__row' : 'rx-review__row';
      group.append(term, detail, price);
      list.append(group);
    }
  }

  /**
   * The Review & Confirm screen.
   *
   * Every figure and every value on it is derived from the same places the
   * rest of the overlay reads, so there is nothing here to keep in step by
   * hand — and nothing a shopper can be shown that differs from what the order
   * will carry.
   */
  #updateReview() {
    this.#updateReviewLenses();
    this.#updateReviewPrescription();
  }

  #updateReviewLenses() {
    const lenses = this.refs.reviewLenses;
    if (!lenses) return;

    // The same rows the selection panel shows, minus the frame — which has a
    // section of its own on this screen.
    this.#renderRows(lenses, this.#chosenSummaryRows(this.#currentPricing()));
  }

  /**
   * The prescription read back to the shopper, in the shape an optometrist
   * wrote it. Nothing to show unless they typed one in by hand: a photograph is
   * not ours to read, and a non-prescription order has none.
   */
  #updateReviewPrescription() {
    const section = this.refs.reviewPrescriptionSection;
    const table = this.refs.reviewPrescription;
    if (!section || !table) return;

    const prescription = buildPrescriptionTable(this.#navigator.data);
    section.hidden = prescription == null;

    if (!prescription) {
      table.replaceChildren();
      return;
    }

    const head = document.createElement('thead');
    const headings = document.createElement('tr');
    // The empty corner above the eye labels.
    headings.append(document.createElement('td'));

    for (const column of prescription.columns) {
      const heading = document.createElement('th');
      heading.scope = 'col';
      heading.textContent = column;
      headings.append(heading);
    }

    head.append(headings);

    const body = document.createElement('tbody');
    for (const row of prescription.rows) {
      body.append(this.#buildPrescriptionRow(row.label, row.values));
    }

    for (const prismRow of this.#buildPrismRows(prescription.columns.length)) {
      body.append(prismRow);
    }

    table.replaceChildren(head, body);
  }

  /**
   * @param {string} label
   * @param {string[]} values
   */
  #buildPrescriptionRow(label, values) {
    const row = document.createElement('tr');

    const rowLabel = document.createElement('th');
    rowLabel.scope = 'row';
    rowLabel.textContent = label;
    row.append(rowLabel);

    for (const value of values) {
      const cell = document.createElement('td');
      cell.textContent = value === '' ? '—' : value;
      row.append(cell);
    }

    return row;
  }

  /**
   * One row per eye that has prism, spanning the columns.
   *
   * Prism is described in words — a strength and a direction to grind it in —
   * so it does not fit the grid of numbers above it.
   *
   * @param {number} columnCount
   * @returns {HTMLTableRowElement[]}
   */
  #buildPrismRows(columnCount) {
    if (!this.#navigator.data.includesPrism) return [];

    const rows = [];

    for (const eye of [
      { key: 'Right', label: 'Right (OD) Δ' },
      { key: 'Left', label: 'Left (OS) Δ' },
    ]) {
      const summary = buildPrismSummary(this.#navigator.data, { eye: eye.key });
      if (!summary) continue;

      const row = document.createElement('tr');

      const rowLabel = document.createElement('th');
      rowLabel.scope = 'row';
      rowLabel.textContent = eye.label;

      const cell = document.createElement('td');
      cell.colSpan = columnCount;
      cell.textContent = summary;

      row.append(rowLabel, cell);
      rows.push(row);
    }

    return rows;
  }

  /** @param {string} stepId */
  #currentStepIs(stepId) {
    return this.#navigator.currentStep.id === stepId;
  }

  #currentStepElement() {
    const currentStepId = this.#navigator.currentStep.id;
    return this.refs.steps.find((stepElement) => stepElement.dataset.stepId === currentStepId);
  }

  /**
   * Put the configured frame in the bag.
   *
   * Four requests at most, in an order that matters:
   *
   * 1. Read the cart, for the note it is already carrying. That note is one
   *    field for the whole order, so a second pair of glasses must not wipe
   *    what the first left there.
   * 2. Add the line. One line: the frame, carrying the whole configuration as
   *    properties, which a Cart Transform Function then splits into frame and
   *    lenses. Adding the lenses ourselves would bill for them twice.
   * 3. Save the note and cart attributes through `/cart/update.js`, after the
   *    line exists. `/cart/add.js` does not update either cart-level field.
   * 4. Read the cart and announce the addition, which refreshes the bubble and
   *    drawer.
   *
   * We do nothing to open the drawer. It opens itself on that last event, and
   * already knows to wait for a modal dialog to close first — see
   * `cart-drawer.js` — which is this case exactly, so closing is enough.
   */
  async #submitCompletion() {
    if (this.#isAddingToCart || this.#completionSubmitted || this.#completionError) return;
    this.#isAddingToCart = true;
    this.#setContinueBusy(true);
    try {
      const link = readCompletionLink(window.location.search);
      const url = completionSubmitUrl(this.dataset.completionBase, window.location.origin);
      const result = await submitCompletion(url, buildCompletionPayload(this.#navigator.buildPayload(), link));
      this.#completionSubmitted = true;
      this.closeCamera();
      for (const form of this.refs.steps) form.hidden = true;
      this.querySelector('.rx-overlay__tray').hidden = true;
      const status = this.querySelector('[data-completion-status]');
      status.textContent = completionSuccessMessage(result);
      status.hidden = false;
      status.focus();
    } catch (error) {
      this.#showMessage(error.message || 'Your prescription could not be submitted. Please try again.');
    } finally {
      this.#isAddingToCart = false;
      this.#setContinueBusy(this.#completionSubmitted);
    }
  }

  async #addToCart() {
    if (this.#isAddingToCart) return;

    this.#isAddingToCart = true;
    this.#setContinueBusy(true);

    /**
     * Held back until there is something to announce. Made and then rejected
     * without ever being dispatched, it would be a promise nobody is listening
     * to — which the browser reports as an unhandled rejection.
     *
     * @type {{ promise: Promise<Object>, resolve: Function, reject: Function } | null}
     */
    let announcement = null;
    let itemAdded = false;

    try {
      const cartBefore = await this.#fetchCart();
      const { attributes, request } = this.buildOrder(cartBefore);
      const { note, ...addRequest } = request;

      announcement = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(this.dataset.variantId), quantity: 1 }],
          promise: announcement.promise,
        })
      );

      const sectionIds = this.#cartSectionIds();

      // A prescription file can only travel as a native line-item property,
      // and that only works as multipart/form-data — the JSON body every
      // other add uses has no way to carry a File. See `buildFormDataRequest`.
      const hasFile = addRequest.items[0]?.properties?._prescription_pdf_url instanceof File;
      const added = hasFile
        ? await this.#postForm(Theme.routes.cart_add_url, buildFormDataRequest(addRequest, sectionIds))
        : await this.#postJson(Theme.routes.cart_add_url, {
            ...addRequest,
            ...(sectionIds ? { sections: sectionIds } : {}),
          });
      itemAdded = true;
      this.#emit('add_to_cart_success', {
        variant_id: Number(this.dataset.variantId) || null,
        product_id: Number(this.dataset.productId) || null,
        sku: this.dataset.variantSku || null,
        line_price_usd: Math.round(this.#currentPricing().total) / 100,
      });
      this.#linkId = null;

      const noteChanged = note !== (cartBefore.note ?? '');
      if (noteChanged || Object.keys(attributes).length > 0) {
        await this.#updateCart({
          ...(noteChanged ? { note } : {}),
          ...(Object.keys(attributes).length > 0
            ? { attributes: { ...(cartBefore.attributes ?? {}), ...attributes } }
            : {}),
        });
      }

      const cartAfter = await this.#fetchCart();
      if (noteChanged && cartAfter.note !== note) {
        throw new Error('The cart note did not save.');
      }
      announcement.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartAfter),
        detail: {
          items: cartAfter.items,
          source: 'solstice-rx-configurator',
          sourceId: this.id,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: added.sections,
          didError: false,
        },
      });

      this.close();
    } catch (error) {
      announcement?.reject(error);
      if (itemAdded) {
        // The purchase succeeded; later cart failures must never invite another add.
        console.warn('[solstice-rx-configurator] Cart update or refresh failed after adding:', error);
        window.location.assign(Theme.routes.cart_url);
        return;
      }
      this.#emit('add_to_cart_error', {
        error_message: error?.message || String(error),
        error_code: 'cart_add_failed',
      });
      console.error('[solstice-rx-configurator] Add to cart failed:', error);
      this.#showMessage('We could not add this to your bag. Please try again.');
    } finally {
      this.#isAddingToCart = false;
      this.#setContinueBusy(false);
    }
  }

  /**
   * The cart as it stands.
   *
   * @returns {Promise<{ note?: string, attributes?: Object, items?: Array<Object> }>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Could not read the cart (${response.status}).`);
    return response.json();
  }

  /**
   * @param {{ note?: string, attributes?: Object }} body
   * @returns {Promise<Object>}
   */
  #updateCart(body) {
    return this.#postJson(`${Theme.routes.cart_update_url}.js`, body);
  }

  /**
   * A cart request, with the two ways it can fail told apart: a refusal the
   * server describes, and a transport that never got there.
   *
   * @param {string} url
   * @param {Object} body
   * @returns {Promise<Object>}
   */
  async #postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    const result = await response.json().catch(() => ({}));

    // A refusal comes back as 4xx with `description` and `status` on it; an
    // accepted request never carries `status`.
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || `Cart request failed (${response.status}).`);
    }

    return result;
  }

  /**
   * The one cart request that must be multipart: a prescription file sitting
   * in a line-item property. Same two failures as `#postJson`, told apart the
   * same way — only the body differs, and no `Content-Type` is set, so the
   * browser can put its own boundary on it.
   *
   * @param {string} url
   * @param {FormData} formData
   * @returns {Promise<Object>}
   */
  async #postForm(url, formData) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || `Cart request failed (${response.status}).`);
    }

    return result;
  }

  /**
   * Which cart sections the add request should render for us, so the drawer can
   * swap its markup in rather than fetching it separately afterwards.
   *
   * @returns {string} Comma separated, or empty when the page has no cart.
   */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((cartItems) => (cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  /** @param {boolean} isBusy */
  #setContinueBusy(isBusy) {
    const { continueButton } = this.refs;
    if (!continueButton) return;

    continueButton.disabled = isBusy;
    continueButton.setAttribute('aria-busy', String(isBusy));
    if (isBusy) {
      if (!continueButton.dataset.idleLabel) continueButton.dataset.idleLabel = continueButton.textContent;
      continueButton.textContent = this.isCompletion ? 'Submitting…' : 'Adding to bag…';
    } else if (continueButton.dataset.idleLabel) {
      continueButton.textContent = continueButton.dataset.idleLabel;
      delete continueButton.dataset.idleLabel;
    }
    if (this.refs.backButton) this.refs.backButton.disabled = isBusy;
    const forms = this.querySelector('.rx-overlay__step-forms');
    if (forms) forms.inert = isBusy;
  }

  /** @param {import('@theme/solstice-rx-navigator').StepError[]} errors */
  #showErrors(errors) {
    this.#clearMessages();
    this.#showMessage(errors.map((error) => error.message).join(' '));

    for (const error of errors) {
      if (!error.field) continue;
      const field = this.#currentStepElement()?.querySelector(`[name="${CSS.escape(error.field)}"]`);
      if (!field) continue;
      field.setAttribute('aria-invalid', 'true');
      const wrapper = field.closest('.rx-field');
      if (wrapper) {
        const hint = document.createElement('span');
        hint.className = 'rx-field__error';
        hint.dataset.rxFieldError = '';
        hint.id = `${field.id || error.field}-error`;
        hint.textContent = field.value === '' ? 'Required' : error.message;
        wrapper.append(hint);
        field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), hint.id].filter(Boolean).join(' '));
      }
    }

    const offendingField = errors[0]?.field;
    if (!offendingField) return;

    const field = this.#currentStepElement()?.querySelector(`[name="${offendingField}"]`);
    field?.focus({ preventScroll: true });
    this.querySelector('.rx-overlay__scroll')?.scrollTo({ top: 0 });
  }

  /** @param {string} message */
  #showMessage(message) {
    const messageElement = this.#currentStepElement()?.querySelector('[data-step-error]');
    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.hidden = false;
  }

  #clearMessages() {
    for (const field of this.querySelectorAll('[aria-invalid="true"]')) {
      field.removeAttribute('aria-invalid');
      const ids = (field.getAttribute('aria-describedby') || '').split(' ').filter((id) => !this.querySelector('[data-rx-field-error][id="' + CSS.escape(id) + '"]'));
      if (ids.length) field.setAttribute('aria-describedby', ids.join(' '));
      else field.removeAttribute('aria-describedby');
    }
    for (const hint of this.querySelectorAll('[data-rx-field-error]')) hint.remove();
    for (const messageElement of this.querySelectorAll('[data-step-error]')) {
      messageElement.textContent = '';
      messageElement.hidden = true;
    }
  }

  /**
   * The rendered forms and the step list can fall out of step silently — a
   * field renamed in one and not the other keeps working on screen while
   * quietly dropping out of the order. This says so in the console rather than
   * throwing, so a mismatch that slips through never breaks a shopper's
   * checkout.
   *
   * @param {import('@theme/solstice-rx-navigator').StepDefinition[]} steps
   */
  #warnAboutMarkupDrift(steps) {
    for (const problem of findStepMarkupDrift(this, steps)) {
      console.warn(`[solstice-rx-configurator] ${problem}`);
    }
  }
}

/**
 * Pair up the two lists a dynamic price is stored as.
 *
 * They are kept side by side in admin, a price at the same position as the
 * option it belongs to, which is a shape that can silently fall out of step.
 * Pairing them here means nothing downstream has to hold two lists in its head,
 * and a price with no option to match — or the other way about — is dropped
 * rather than carried into a total.
 *
 * The money itself is a metafield, and what JSON makes of one has an `item`
 * around it; the fallback is there for the day that stops being true.
 *
 * @param {HTMLInputElement} input
 * @returns {{ label: string, price: number }[]}
 */
function readDynamicPrices(input) {
  const { dynamicPrice, dynamicPriceLabels } = input.dataset;
  if (!dynamicPrice || !dynamicPriceLabels) return [];

  try {
    const prices = JSON.parse(dynamicPrice);
    const labels = JSON.parse(dynamicPriceLabels);

    return labels
      .map((label, index) => {
        const money = prices[index]?.item ?? prices[index];
        // Stored in whole units of the currency, where everything else in the
        // configurator counts the smallest one.
        const price = Math.round(Number(money?.amount) * 100);

        return Number.isFinite(price) ? { label, price } : null;
      })
      .filter(Boolean);
  } catch (error) {
    console.warn('[solstice-rx-configurator] Could not read a dynamic price:', error);
    return [];
  }
}

if (!customElements.get('solstice-rx-configurator')) {
  customElements.define('solstice-rx-configurator', RxConfigurator);
}
